// src/pages/Reservations.js
// Page réservations + formulaire de demande - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../hooks/useNotifications';

const Reservations = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotifications();

  const listingId = searchParams.get('listingId');
  const listingTitle = searchParams.get('title');

  const [formData, setFormData] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isFormMode = !!listingId;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.clientName.trim()) newErrors.clientName = t('errors.required');
    if (!formData.clientPhone.trim()) newErrors.clientPhone = t('errors.required');
    if (!formData.checkInDate) newErrors.checkInDate = t('errors.required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      showError(t('common.error'), t('errors.validationError'));
      return;
    }

    setSubmitting(true);
    try {
      // TODO: Appeler l'API de réservation
      // await reservationsAPI.create({ ...formData, listingId });

      // Simulation
      await new Promise((resolve) => setTimeout(resolve, 1000));

      showSuccess(t('common.success'), t('reservation.success'));
      setFormData({
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        checkInDate: '',
        checkOutDate: '',
        numberOfGuests: '',
        message: '',
      });
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      showError(t('common.error'), t('reservation.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px',
      position: 'sticky',
      top: 0,
      backgroundColor: '#0A0E1A',
      zIndex: 50,
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: '#FFFFFF',
      fontSize: '20px',
      cursor: 'pointer',
      padding: '4px',
    },
    title: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF' },
    listingInfo: {
      margin: '16px',
      padding: '12px 16px',
      backgroundColor: 'rgba(255, 215, 0, 0.1)',
      border: '1px solid rgba(255, 215, 0, 0.3)',
      borderRadius: '10px',
      fontSize: '14px',
      color: '#FFD700',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      padding: '16px',
    },
    field: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#A0AEC0' },
    required: { color: '#F56565' },
    input: {
      padding: '12px 16px',
      backgroundColor: '#111827',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      color: '#FFFFFF',
      fontSize: '15px',
      outline: 'none',
    },
    inputError: { border: '1px solid #F56565' },
    textarea: {
      padding: '12px 16px',
      backgroundColor: '#111827',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      color: '#FFFFFF',
      fontSize: '15px',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
      fontFamily: 'inherit',
    },
    errorText: { fontSize: '12px', color: '#F56565' },
    submitButton: {
      padding: '16px',
      backgroundColor: '#FFD700',
      border: 'none',
      borderRadius: '10px',
      color: '#0A0E1A',
      fontSize: '16px',
      fontWeight: '700',
      cursor: 'pointer',
      marginTop: '8px',
    },
    emptyContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '64px 24px',
      textAlign: 'center',
      gap: '12px',
    },
    emptyIcon: { fontSize: '56px', opacity: 0.4 },
    emptyTitle: { fontSize: '16px', fontWeight: '600', color: '#FFFFFF' },
    emptyText: { fontSize: '14px', color: '#A0AEC0', maxWidth: '280px', lineHeight: '1.5' },
  };

  // Mode formulaire (arrivée depuis une annonce)
  if (isFormMode) {
    return (
      <div style={styles.container}>
        <Helmet>
          <title>Réserver | Fynho</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
          <div style={styles.title}>{t('reservation.title')}</div>
        </div>

        {listingTitle && (
          <div style={styles.listingInfo}>
            🏡 {decodeURIComponent(listingTitle)}
          </div>
        )}

        <div style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>
              {t('reservation.fullName')} <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              style={{ ...styles.input, ...(errors.clientName ? styles.inputError : {}) }}
              value={formData.clientName}
              onChange={(e) => handleChange('clientName', e.target.value)}
              placeholder="Ex: Jean Kamga"
            />
            {errors.clientName && <span style={styles.errorText}>{errors.clientName}</span>}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>
              {t('reservation.phone')} <span style={styles.required}>*</span>
            </label>
            <input
              type="tel"
              style={{ ...styles.input, ...(errors.clientPhone ? styles.inputError : {}) }}
              value={formData.clientPhone}
              onChange={(e) => handleChange('clientPhone', e.target.value)}
              placeholder="Ex: +237 6XX XXX XXX"
            />
            {errors.clientPhone && <span style={styles.errorText}>{errors.clientPhone}</span>}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>{t('reservation.email')}</label>
            <input
              type="email"
              style={styles.input}
              value={formData.clientEmail}
              onChange={(e) => handleChange('clientEmail', e.target.value)}
              placeholder="Ex: jean@email.com"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>
              {t('reservation.checkInDate')} <span style={styles.required}>*</span>
            </label>
            <input
              type="date"
              style={{ ...styles.input, ...(errors.checkInDate ? styles.inputError : {}) }}
              value={formData.checkInDate}
              onChange={(e) => handleChange('checkInDate', e.target.value)}
            />
            {errors.checkInDate && <span style={styles.errorText}>{errors.checkInDate}</span>}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>{t('reservation.checkOutDate')}</label>
            <input
              type="date"
              style={styles.input}
              value={formData.checkOutDate}
              onChange={(e) => handleChange('checkOutDate', e.target.value)}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>{t('reservation.numberOfGuests')}</label>
            <input
              type="number"
              style={styles.input}
              value={formData.numberOfGuests}
              onChange={(e) => handleChange('numberOfGuests', e.target.value)}
              placeholder="Ex: 2"
              min="1"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>{t('reservation.message')}</label>
            <textarea
              style={styles.textarea}
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Précisez vos besoins..."
            />
          </div>

          <button
            style={{ ...styles.submitButton, opacity: submitting ? 0.6 : 1 }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? t('common.loading') : t('reservation.submit')}
          </button>
        </div>
      </div>
    );
  }

  // Mode liste (onglet Voyages depuis BottomNav)
  return (
    <div style={styles.container}>
      <Helmet>
        <title>Mes voyages | Fynho</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <div style={styles.title}>🧳 {t('nav.voyages')}</div>
      </div>

      <div style={styles.emptyContainer}>
        <div style={styles.emptyIcon}>🧳</div>
        <div style={styles.emptyTitle}>Aucune réservation</div>
        <div style={styles.emptyText}>
          Vos demandes de réservation apparaîtront ici. Explorez les annonces pour commencer!
        </div>
      </div>
    </div>
  );
};

export default Reservations;